import { publishAuthorizationContext } from "./authorizationContext.js";
export { onAuthorizationContextChange } from "./authorizationContext.js";
/**
 * Caller's RBAC posture. Hits `GET /v1/me:permissions` (the
 * GetMyPermissions RPC) and caches the response in-memory, mirroring
 * the cache/hook shape `userInfo.js` uses for `/v1/me`.
 *
 * UI gates read from the flat `permissions` SET — never role names.
 * Custom roles arrive in RBAC v2, so name-gating ("if role === admin")
 * would silently break the moment a tenant defines its own role. Role
 * names are used only for *explanatory* copy (the binding role names via
 * `roleNames` → `denialReason.roleClause`), never to decide what to
 * show. `roleCatalog` is parsed from the wire for a future role-explainer
 * surface and is not yet read by any consumer.
 *
 * Degradation distinguishes two cases:
 *   - UNAVAILABLE: the route is genuinely absent (local dev with no
 *     auth, or a pre-RBAC tenant whose gateway 404s). This is the
 *     before-RBAC world, so every permission is treated as granted and
 *     sensitive reads are shown — the dashboard behaves exactly as it
 *     did pre-RBAC. This posture is cacheable.
 *   - DEGRADED: a transient failure (401/403/5xx/network) against a
 *     tenant that may well be on RBAC. Affordances still fail OPEN (so a
 *     blip doesn't flash-hide the whole UI), but sensitive reads fail
 *     CLOSED (don't reveal a value we can't confirm the viewer may see),
 *     and mounted, visible consumers share bounded retries until recovery.
 *
 * Gating (hide-by-default) otherwise engages only once we hold a
 * concrete permission set that lacks the permission; a denied click in
 * the fail-open window still surfaces gracefully via `denialReason.js`
 * rather than a raw 403.
 * Effective scoped-grant degradation follows the same rule for affordances,
 * while sensitive data remains fail-closed.
 *
 * Sensitive DATA (raw event payloads today; prompt/response text is a
 * follow-up surface) fails CLOSED while the set is loading/degraded — it
 * is never revealed before we can confirm the viewer may see it.
 */
import { useEffect, useSyncExternalStore } from "react";
import { API_BASE_URL, AUTH_REQUIRED } from "./featureFlags.js";
import { authedFetch } from "./restClient.js";
import { getAuthIdentityEpoch, onAuthIdentityChange } from "./authTokenStore.js";
import { PERMISSIONS } from "./permissionKeys.js";

const PERMISSIONS_URL = `${API_BASE_URL.replace(/\/$/, "")}/v1/me:permissions`;
// Keep the posture cheap to share across the SPA, but never pin another
// administrator's grant change for the rest of this browser session.
const RESOLVED_CACHE_TTL_MS = 30_000;
// Revalidate shortly before the hard expiry. The last confirmed posture can
// stay rendered during this window, so sensitive rows do not flash away on a
// healthy background refresh, while the 30-second authorization bound remains
// unchanged if the request stalls.
const RESOLVED_CACHE_REVALIDATE_MS = 25_000;

// Resolved-but-no-data posture: the route isn't reachable (local dev,
// pre-RBAC tenant, or a fetch error). `available: false` drives
// fail-open in `hasPermission`. Frozen + shared so identity checks and
// `useState` initialisers stay cheap.
const UNAVAILABLE = Object.freeze({
    available: false,
    loading: false,
    degraded: false,
    permissions: new Set(),
    unscoped: false,
    bindings: [],
    roleCatalog: [],
    visibleEndpointCount: null,
    scopeNamesDegraded: false,
    subjectScope: null,
    subjectScopeDegraded: false,
    effectivePermissionScopes: [],
    deniedSurfaces: new Set(),
    activeEscalations: [],
    isEscalationApprover: false,
});

// In-flight posture, returned by the hook before the first fetch
// resolves. Distinct from UNAVAILABLE (`loading: true`) so sensitive
// surfaces can fail CLOSED while the set is still unknown.
const LOADING = Object.freeze({ ...UNAVAILABLE, loading: true });

// Transient-failure posture (401/403/5xx/network) — affordances fail
// open, sensitive reads fail closed, and getMyPermissions does NOT cache
// it (so the next mount retries instead of pinning the session open).
const DEGRADED = Object.freeze({ ...UNAVAILABLE, degraded: true });

// DEGRADED for a request that never produced a response (connection reset or
// closed mid-flight, as when the gateway pod is replaced). Unlike a status it
// says nothing about the grant, so a still-valid confirmed posture survives it.
const UNREACHABLE = Object.freeze({ ...DEGRADED });

export {
    UNAVAILABLE as PERMISSIONS_UNAVAILABLE,
    LOADING as PERMISSIONS_LOADING,
    DEGRADED as PERMISSIONS_DEGRADED,
};

let _cache = null;
let _snapshot = LOADING;
let _inflight = null;
let _revision = 0;
let _requestGeneration = 0;
let _controller = null;
let _cacheRevalidateTimer = null;
let _cacheExpiryTimer = null;
let _retryTimer = null;
let _retryAttempt = 0;
let _cacheRefreshDue = false;
let _successAt = 0;
const _listeners = new Set();

function canonical(value) {
    if (Array.isArray(value)) return value.map(canonical).sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
    if (value && typeof value === "object") return Object.fromEntries(Object.keys(value).sort().map((key) => [key, canonical(value[key])]));
    return value;
}

export function authorizationContextKey(caps) {
    const pin = caps?.subjectScope?.pin;
    return JSON.stringify(canonical({
        identity: getAuthIdentityEpoch(), available: Boolean(caps?.available),
        loading: Boolean(!caps || caps.loading), degraded: Boolean(caps?.degraded || caps?.subjectScopeDegraded),
        permissions: [...(caps?.permissions ?? [])], unscoped: Boolean(caps?.unscoped),
        scopes: caps?.effectivePermissionScopes ?? [], denied: [...(caps?.deniedSurfaces ?? [])],
        disposition: caps?.subjectScope?.disposition,
        includeDeprovisioned: caps?.subjectScope?.includeDeprovisioned,
        groups: caps?.subjectScope?.grantedGroups?.map((group) => group.group),
        generation: pick(pin, "generation_id", "generationId"),
        grantEpoch: pick(pin, "grant_epoch", "grantEpoch"),
    }));
}

function publish(caps, refreshing = false) {
    const previousKey = authorizationContextKey(_snapshot);
    _snapshot = refreshing ? { ...caps, refreshing: true } : caps;
    if (previousKey !== authorizationContextKey(_snapshot)) {
        publishAuthorizationContext(sensitiveAccess(_snapshot));
    }
    _revision++;
    for (const listener of _listeners) listener();
}

function visible() { return typeof document === "undefined" || document.visibilityState !== "hidden"; }
function clearRetry() {
    if (_retryTimer !== null) clearTimeout(_retryTimer);
    _retryTimer = null;
}
function scheduleRetry() {
    if (_retryTimer !== null) return;
    if (!_listeners.size || !visible()) return;
    const delay = Math.min(30_000, 1000 * 2 ** Math.min(_retryAttempt++, 5));
    _retryTimer = setTimeout(() => {
        _retryTimer = null;
        getMyPermissions();
    }, Math.min(30_000, delay * (0.9 + Math.random() * 0.2)));
}
function resumeVerification() {
    if (!visible()) { clearRetry(); return; }
    if (!_cache || _cacheRefreshDue || (_cache.available && Date.now() - _successAt >= RESOLVED_CACHE_REVALIDATE_MS)) refreshMyPermissions();
}
function subscribePermissions(listener) {
    _listeners.add(listener);
    if (_listeners.size === 1) {
        globalThis.window?.addEventListener("focus", resumeVerification);
        globalThis.window?.addEventListener("online", resumeVerification);
        globalThis.document?.addEventListener("visibilitychange", resumeVerification);
    }
    return () => {
        _listeners.delete(listener);
        if (!_listeners.size) {
            clearRetry();
            globalThis.window?.removeEventListener("focus", resumeVerification);
            globalThis.window?.removeEventListener("online", resumeVerification);
            globalThis.document?.removeEventListener("visibilitychange", resumeVerification);
        }
    };
}
function getPermissionsRevision() { return _revision; }
function clearCacheTimers() {
    clearTimeout(_cacheRevalidateTimer);
    clearTimeout(_cacheExpiryTimer);
    _cacheRevalidateTimer = _cacheExpiryTimer = null;
}
function expirePosture() {
    clearCacheTimers();
    if (_inflight) {
        // The revalidation that RESOLVED_CACHE_REVALIDATE_MS started is still
        // running under its own RESOLVED_CACHE_TTL_MS deadline, so this timer
        // fires only 5s into a request allowed 30s. Degrading here would flip
        // the authorization context and tear down every region keyed on it,
        // including a running findings review. Keep the last resolved posture
        // instead: the request degrades it itself if it truly fails.
        _cacheRefreshDue = true;
        return;
    }
    _cache = null;
    _cacheRefreshDue = false;
    publish(DEGRADED);
    scheduleRetry();
}
function scheduleCacheExpiry() {
    clearCacheTimers();
    if (!_cache?.available) return;
    _successAt = Date.now();
    _cacheRevalidateTimer = setTimeout(() => {
        _cacheRefreshDue = true;
        // A mounted investigation can be reviewing while its tab is hidden.
        // Keep healthy refreshes running; only failure retries pause on hide.
        if (_listeners.size) getMyPermissions();
    }, RESOLVED_CACHE_REVALIDATE_MS);
    _cacheExpiryTimer = setTimeout(expirePosture, RESOLVED_CACHE_TTL_MS);
}

export function invalidateMyPermissions() {
    clearCacheTimers();
    clearRetry();
    _requestGeneration++;
    _controller?.abort();
    _inflight = null;
    _cache = null;
    _cacheRefreshDue = false;
    _retryAttempt = 0;
    publish(LOADING);
}
onAuthIdentityChange(invalidateMyPermissions);

export function refreshMyPermissions() {
    clearRetry();
    _cacheRefreshDue = true;
    return getMyPermissions();
}

// grpc-gateway's default JSON encoder emits camelCase (`scopeGroups`),
// but accept snake_case too — `userInfo.js` carries the same dual-shape
// guard in case the gateway ever flips `original_name=true`.
function pick(obj, snake, camel) {
    if (!obj) return undefined;
    return obj[camel] ?? obj[snake];
}

function transformScopeGroup(g) {
    return {
        id: g?.id ?? "",
        // Empty when the directory hasn't synced this group yet; callers
        // render `id` as the fallback (NOT a degraded-names signal).
        name: g?.name ?? "",
    };
}

function transformBinding(b) {
    const groups = pick(b, "scope_groups", "scopeGroups") ?? [];
    return {
        role: b?.role ?? "",
        scopeGroups: Array.isArray(groups) ? groups.map(transformScopeGroup) : [],
    };
}

function transformRole(r) {
    return {
        name: r?.name ?? "",
        description: r?.description ?? "",
        permissions: Array.isArray(r?.permissions) ? r.permissions : [],
    };
}

function count(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

function enumTail(value, prefix) {
    if (typeof value !== "string") return "unspecified";
    return value.startsWith(prefix) ? value.slice(prefix.length).toLowerCase() : value.toLowerCase();
}

function transformGroupKey(group) {
    return {
        sourceProvider: pick(group, "source_provider", "sourceProvider") ?? "",
        integrationId: pick(group, "integration_id", "integrationId") ?? "",
        sourceId: pick(group, "source_id", "sourceId") ?? "",
    };
}

function transformSubjectScope(scope) {
    if (!scope || typeof scope !== "object") return null;
    const groups = pick(scope, "granted_groups", "grantedGroups") ?? [];
    const denied = pick(scope, "denied_surfaces", "deniedSurfaces") ?? [];
    const grantedGroups = Array.isArray(groups) ? groups.map((group) => ({
        groupSourceId: pick(group, "group_source_id", "groupSourceId") ?? "",
        memberCount: count(pick(group, "member_count", "memberCount")),
        presence: enumTail(group.presence, "GROUP_PRESENCE_"),
        group: transformGroupKey(group.group),
    })) : [];
    const rawGroupTotal = pick(scope, "granted_group_total_count", "grantedGroupTotalCount");
    const requiredPermissions = pick(scope, "required_permissions", "requiredPermissions") ?? [];
    return {
        disposition: enumTail(
            scope.disposition,
            "VIEWER_SCOPE_DISPOSITION_",
        ),
        effectiveSubjectCount: count(pick(scope, "effective_subject_count", "effectiveSubjectCount")),
        grantedGroups,
        grantedGroupTotalCount: rawGroupTotal == null ? grantedGroups.length : count(rawGroupTotal),
        includeDeprovisioned: Boolean(pick(scope, "include_deprovisioned", "includeDeprovisioned")),
        pin: scope.pin ?? null,
        deniedSurfaces: Array.isArray(denied) ? denied.map((surface) => ({
            rpcName: pick(surface, "rpc_name", "rpcName") ?? "",
            reason: enumTail(surface.reason, "CLIENT_SCOPE_DENIAL_REASON_"),
        })).filter((surface) => surface.rpcName) : [],
        requiredPermissions: Array.isArray(requiredPermissions) ? requiredPermissions : [],
    };
}

function transformActiveEscalation(raw) {
    const target = raw?.target ?? {};
    const subject = target.subject ?? null;
    const expires = new Date(pick(raw, "expires_at", "expiresAt"));
    return {
        requestId: pick(raw, "request_id", "requestId") ?? "",
        role: raw?.role ?? "",
        subject: subject ? {
            sourceProvider: pick(subject, "source_provider", "sourceProvider") ?? "",
            integrationId: pick(subject, "integration_id", "integrationId") ?? "",
            sourceId: pick(subject, "source_id", "sourceId") ?? "",
            displayName: pick(subject, "display_name", "displayName") ?? "",
        } : null,
        endpointId: pick(target, "endpoint_id", "endpointId") ?? "",
        expiresAt: Number.isFinite(expires.getTime()) ? expires : null,
    };
}

function transformEffectivePermissionScope(scope) {
    const groups = Array.isArray(scope?.groups) ? scope.groups : [];
    return {
        permission: scope?.permission ?? "",
        groups: groups.map(transformGroupKey),
    };
}

// GetMyPermissionsResponse → the cached posture object. `available:
// true` flips gating on.
export function transformPermissions(json) {
    if (!json || typeof json !== "object") return UNAVAILABLE;
    const perms = new Set(Array.isArray(json.permissions) ? json.permissions : []);
    const bindings = Array.isArray(json.bindings) ? json.bindings : [];
    const roleCatalog = pick(json, "role_catalog", "roleCatalog") ?? [];
    // optional uint32: absent when the count query failed — render "—",
    // don't coerce to 0 (which would read as "you can see no endpoints").
    const rawCount = pick(json, "visible_endpoint_count", "visibleEndpointCount");
    const subjectScope = transformSubjectScope(pick(json, "subject_scope", "subjectScope"));
    const rawEffectiveScopes = pick(
        json,
        "effective_permission_scopes",
        "effectivePermissionScopes",
    ) ?? [];
    const effectivePermissionScopes = Array.isArray(rawEffectiveScopes)
        ? rawEffectiveScopes.map(transformEffectivePermissionScope)
        : [];
    // Approval authority can come from membership of a configured directory
    // group, which grants no role and so contributes nothing to the union above.
    // Fold it in as the permission it confers, the same way the effective scoped
    // grants are folded in, so every gate reads one set instead of special-casing
    // a second source of the same authority.
    const isEscalationApprover = Boolean(
        pick(json, "is_escalation_approver", "isEscalationApprover"),
    );
    if (isEscalationApprover) perms.add(PERMISSIONS.ESCALATIONS_APPROVE);
    const rawEscalations = pick(json, "active_escalations", "activeEscalations") ?? [];
    const activeEscalations = (Array.isArray(rawEscalations) ? rawEscalations : [])
        .map(transformActiveEscalation)
        .filter((escalation) => escalation.expiresAt);
    for (const scope of effectivePermissionScopes) {
        if (scope.permission) perms.add(scope.permission);
    }
    // A scoped directory-group grant is intentionally absent from the legacy
    // authentication-time union. The self-scope summary names the permission
    // expression the runtime actually resolved; expose those capabilities only
    // when it produced a real non-empty scope.
    if (subjectScope?.disposition === "scoped") {
        for (const permission of subjectScope.requiredPermissions) perms.add(permission);
    }
    return {
        available: true,
        loading: false,
        degraded: false,
        permissions: perms,
        unscoped: Boolean(json.unscoped),
        bindings: bindings.map(transformBinding),
        roleCatalog: Array.isArray(roleCatalog) ? roleCatalog.map(transformRole) : [],
        visibleEndpointCount: typeof rawCount === "number" ? rawCount : null,
        scopeNamesDegraded: Boolean(pick(json, "scope_names_degraded", "scopeNamesDegraded")),
        subjectScope,
        subjectScopeDegraded: Boolean(pick(json, "subject_scope_degraded", "subjectScopeDegraded")),
        effectivePermissionScopes,
        deniedSurfaces: new Set(subjectScope?.deniedSurfaces.map((surface) => surface.rpcName) ?? []),
        activeEscalations,
        isEscalationApprover,
    };
}

async function fetchPermissions(signal) {
    // No auth → no signed-in identity to resolve a permission set for.
    // Pre-RBAC behaviour is full access, so fail open (cacheable).
    if (!AUTH_REQUIRED) return UNAVAILABLE;
    let res;
    try {
        // authedFetch carries the bearer + 401-refresh dance; it throws
        // AuthMissingError when no token is in the store yet.
        // freshToken: this read repeats every 25s, so without it every access
        // token expiry costs a 401 plus a refresh-and-retry round trip.
        res = await authedFetch(PERMISSIONS_URL, { signal, freshToken: true });
    } catch (err) {
        console.warn("[permissions] /v1/me:permissions unreachable — degraded", err?.name ?? err);
        return err?.name === "AuthMissingError" ? DEGRADED : UNREACHABLE;
    }
    // Only the request itself may classify as UNREACHABLE: a response that
    // arrived but cannot be read is an answer, so it fails closed.
    try {
        // 404 = route genuinely absent (pre-RBAC gateway) → cacheable
        // fail-open. Any OTHER non-2xx (401 after a failed refresh, 5xx)
        // is a transient failure against a possibly-RBAC tenant → DEGRADED
        // (not cached; sensitive reads fail closed).
        if (res.status === 404) return UNAVAILABLE;
        if (!res.ok) {
            console.warn(`[permissions] /v1/me:permissions ${res.status} — degraded (affordances fail open, sensitive data fails closed)`);
            return DEGRADED;
        }
        // A 2xx with a missing/non-object body is anomalous — treat it as
        // transient (DEGRADED, not cached) rather than letting it become a
        // cached fail-open UNAVAILABLE. The cacheable fail-open posture is
        // reserved strictly for the 404 / no-auth routes.
        const json = await res.json().catch(() => null);
        if (!json || typeof json !== "object" || Array.isArray(json) || (json.permissions != null && !Array.isArray(json.permissions))) return DEGRADED;
        return transformPermissions(json);
    } catch (err) {
        console.warn("[permissions] /v1/me:permissions unreadable — degraded", err?.name ?? err);
        return DEGRADED;
    }
}

// Cached, deduped fetch. Mirrors `userInfo.getCurrentUser`: one
// in-flight promise shared across concurrent callers. Caches definitive
// postures (available, or pre-RBAC unavailable) but NOT a transient
// DEGRADED; mounted, visible consumers share bounded recovery retries.
export function getMyPermissions() {
    if (_cache && !_cacheRefreshDue) return Promise.resolve(_cache);
    if (_inflight) return _inflight;
    const generation = _requestGeneration;
    const identity = getAuthIdentityEpoch();
    _controller = new AbortController();
    const controller = _controller;
    const timeout = setTimeout(() => {
        if (generation === _requestGeneration && _controller === controller) {
            _requestGeneration++;
            controller.abort();
            _inflight = null;
            expirePosture();
        }
    }, RESOLVED_CACHE_TTL_MS);
    const request = fetchPermissions(controller.signal).then((caps) => {
        if (generation !== _requestGeneration || identity !== getAuthIdentityEpoch()) return LOADING;
        if (caps === UNREACHABLE && _cache?.available && Date.now() - _successAt < RESOLVED_CACHE_TTL_MS) {
            // Keep the confirmed posture and retry. Its expiry timer still
            // runs, so the 30-second authorization bound is unchanged.
            _cacheRefreshDue = true;
            scheduleRetry();
            return _cache;
        }
        if (caps.degraded || caps.subjectScopeDegraded) {
            clearCacheTimers();
            _cache = null;
            _cacheRefreshDue = false;
            publish(caps);
            scheduleRetry();
        } else {
            _cache = caps;
            _cacheRefreshDue = false;
            _retryAttempt = 0;
            clearRetry();
            scheduleCacheExpiry();
            publish(caps);
        }
        return caps;
    }).finally(() => {
        clearTimeout(timeout);
        if (_inflight === request) _inflight = null;
    });
    _inflight = request;
    if (_cache) publish(_cache, true);
    return request;
}

// ── Pure predicates (the gating contract) ───────────────────────────

// Affordances + nav. Fail-open whenever the set isn't resolved
// (loading OR unavailable): a non-RBAC tenant must never flash-hide its
// whole UI on every load, and on an RBAC tenant the brief show-then-hide
// window on a denied surface is caught by `denialReason.js` if clicked.
export function hasPermission(caps, perm) {
    if (!caps || !caps.available || caps.subjectScopeDegraded) return true;
    return caps.permissions.has(perm);
}

// Sensitive *data* (raw event payloads today; prompt/response text is a
// follow-up). Fails CLOSED whenever the set isn't definitively known —
// null caps, loading, or a transient DEGRADED posture — so a value is
// never revealed before we can confirm the viewer may see it. Stays open
// ONLY for a genuine pre-RBAC UNAVAILABLE (where redaction doesn't apply).
export function sensitiveAccess(caps, perm = "tenant.ai_sessions.read.sensitive") {
    const status = !caps || caps.loading ? "checking"
        : caps.degraded || caps.subjectScopeDegraded ? "temporarily_unavailable"
            : !caps.available || caps.permissions.has(perm) ? "allowed" : "denied";
    return { status, refreshing: Boolean(caps?.refreshing), contextKey: authorizationContextKey(caps) };
}

export function getSensitiveAccess() { return sensitiveAccess(_snapshot); }

export function hasSensitivePermission(caps, perm) {
    return sensitiveAccess(caps, perm).status === "allowed";
}

// Role name strings the caller holds, in resolver order. For
// explanatory copy only — never gate on these.
export function roleNames(caps) {
    if (!caps || !Array.isArray(caps.bindings)) return [];
    return caps.bindings.map((b) => b.role).filter(Boolean);
}

// This posture is cached for up to 30 seconds, so an entry can outlive its own
// window. Both selectors split on the clock rather than on the list's presence.
export function liveEscalations(caps, now = Date.now()) {
    if (!caps?.available || !Array.isArray(caps.activeEscalations)) return [];
    return caps.activeEscalations.filter((e) => e.expiresAt.getTime() > now);
}

export function lapsedEscalations(caps, now = Date.now()) {
    if (!caps?.available || !Array.isArray(caps.activeEscalations)) return [];
    return caps.activeEscalations.filter((e) => e.expiresAt.getTime() <= now);
}

// Machine-readable navigation suppression for surfaces the backend cannot
// row-filter for this caller. Unknown/degraded posture does not guess; the
// server remains authoritative and a click still receives typed denial copy.
export function isSurfaceDenied(caps, rpcName) {
    return Boolean(caps?.available && caps.deniedSurfaces instanceof Set && caps.deniedSurfaces.has(rpcName));
}

// ── React bindings ──────────────────────────────────────────────────

// Resolves to the cached posture, kicking off the fetch on first use.
// Returns LOADING until the first fetch settles, then the resolved
// posture (which may be UNAVAILABLE → fail-open).
export function useMyPermissions() {
    const revision = useSyncExternalStore(subscribePermissions, getPermissionsRevision, getPermissionsRevision);
    useEffect(() => {
        if (_snapshot === LOADING || (_cache && _cacheRefreshDue)) getMyPermissions();
        else if (!_cache && !_inflight) scheduleRetry();
    }, [revision]);
    return _snapshot;
}

// Boolean visibility gate for a single permission, bound to the live
// posture. Fails OPEN while the set isn't resolved (loading / unavailable
// / degraded) — same contract as `hasPermission`, so nav and affordances
// never flash-hide on a pre-RBAC tenant or a transient blip.
export function useHasPermission(perm) {
    const caps = useMyPermissions();
    return hasPermission(caps, perm);
}

// Boolean visibility gate for ANY of several permissions — true when the
// caller holds at least one. Fails OPEN while the set isn't resolved
// (the first `hasPermission` short-circuits true), matching the
// single-perm hook. An empty list resolves to false (no permission can
// be held), which keeps a misconfigured caller from showing everything.
export function useHasAnyPermission(perms) {
    const caps = useMyPermissions();
    const list = Array.isArray(perms) ? perms : [];
    return list.some((perm) => hasPermission(caps, perm));
}

// Test-only: drop the module-level cache so each test starts cold.
export function _resetForTests() {
    invalidateMyPermissions();
}
